import {
  cn,
  type Path as RouterPath,
  type SetURLSearchParams,
  type UseSearchParamsFn,
} from "@repo/ui";
import {
  Link as TanstackLink,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import type React from "react";
import { forwardRef, useCallback, useMemo } from "react";

type SearchParamsInit =
  | string
  | string[][]
  | Record<string, string | string[]>
  | URLSearchParams;

type NavLinkRenderState = {
  isActive: boolean;
  isPending: boolean;
  isTransitioning: boolean;
};

type BaseTanstackLinkProps = React.ComponentProps<typeof TanstackLink>;

function toURLSearchParams(init?: SearchParamsInit) {
  if (!init) {
    return new URLSearchParams();
  }
  if (
    init instanceof URLSearchParams ||
    Array.isArray(init) ||
    typeof init === "string"
  ) {
    return new URLSearchParams(init);
  }

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(init)) {
    if (Array.isArray(value)) {
      for (const v of value) {
        params.append(key, v);
      }
    } else if (value !== undefined) {
      params.append(key, value);
    }
  }
  return params;
}

export const routerCtxUseTanstackSearchParams: UseSearchParamsFn = (
  defaultInit,
) => {
  const location = useRouterState({ select: (s) => s.location });
  const navigate = useNavigate();

  const searchParams = useMemo(() => {
    const current = location.searchStr ?? "";
    if (!current) {
      return toURLSearchParams(defaultInit);
    }
    const value = current.startsWith("?") ? current.slice(1) : current;
    return toURLSearchParams(value);
  }, [defaultInit, location.searchStr]);

  const setSearchParams = useCallback<SetURLSearchParams>(
    (nextInit, navigateOpts) => {
      const current = location.searchStr ?? "";
      let previousStr: string | undefined;
      if (current) {
        previousStr = current.startsWith("?") ? current.slice(1) : current;
      }
      const previous = toURLSearchParams(previousStr);
      const init =
        typeof nextInit === "function" ? nextInit(previous) : nextInit;
      const nextParams = toURLSearchParams(init);
      const searchString = nextParams.toString();
      const target =
        `${location.pathname}` +
        (searchString ? `?${searchString}` : "") +
        (location.hash ?? "");
      void navigate({
        to: target,
        replace: navigateOpts?.replace,
        state: navigateOpts?.state,
      });
    },
    [location.hash, location.pathname, location.searchStr, navigate],
  );

  return [searchParams, setSearchParams];
};

function mergeActiveClassName(
  base:
    | BaseTanstackLinkProps["activeProps"]
    | BaseTanstackLinkProps["inactiveProps"],
  className?: string | undefined,
) {
  if (!className) {
    return base;
  }

  if (typeof base === "function") {
    return () => {
      const resolved = base() ?? {};
      const mergedClassName = cn([resolved.className, className]);
      return { ...resolved, className: mergedClassName || undefined };
    };
  }

  const resolved = base ?? {};
  const mergedClassName = cn([resolved.className, className]);
  return { ...resolved, className: mergedClassName || undefined };
}

function normaliseTo(to: string | Partial<RouterPath>) {
  return typeof to === "string"
    ? to
    : `${to.pathname ?? ""}${to.search ?? ""}${to.hash ?? ""}`;
}

type NavLinkProps = Omit<
  BaseTanstackLinkProps,
  "children" | "className" | "activeProps" | "inactiveProps" | "to"
> & {
  to: string | Partial<RouterPath>;
  className?: string | ((state: NavLinkRenderState) => string | undefined);
  children?: React.ReactNode | ((state: NavLinkRenderState) => React.ReactNode);
  activeProps?: BaseTanstackLinkProps["activeProps"];
  inactiveProps?: BaseTanstackLinkProps["inactiveProps"];
  end?: boolean;
};

export const RouterCtxNavLink = forwardRef<HTMLAnchorElement, NavLinkProps>(
  (
    { className, children, activeProps, inactiveProps, end, to, ...rest },
    ref,
  ) => {
    const { activeOptions, ...linkProps } = rest;
    const resolvedTo = normaliseTo(to);
    const activeClassName =
      typeof className === "function"
        ? className({
            isActive: true,
            isPending: false,
            isTransitioning: false,
          })
        : undefined;
    const inactiveClassName =
      typeof className === "function"
        ? className({
            isActive: false,
            isPending: false,
            isTransitioning: false,
          })
        : undefined;

    return (
      <TanstackLink
        {...linkProps}
        ref={ref}
        className={typeof className === "string" ? className : undefined}
        activeOptions={{
          ...activeOptions,
          exact: end ?? activeOptions?.exact ?? false,
        }}
        to={resolvedTo}
        activeProps={mergeActiveClassName(activeProps, activeClassName)}
        inactiveProps={mergeActiveClassName(inactiveProps, inactiveClassName)}
      >
        {(state) => {
          const navState: NavLinkRenderState = {
            isActive: state.isActive,
            isTransitioning: state.isTransitioning,
            isPending: state.isTransitioning,
          };
          if (typeof children === "function") {
            return children(navState);
          }
          return children;
        }}
      </TanstackLink>
    );
  },
);
RouterCtxNavLink.displayName = "TanstackNavLink";
